import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export interface AdminUserData {
  id: number;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  role: "STUDENT" | "INSTRUCTOR" | "ADMIN" | "SUPERADMIN" | string;
  roles: string[];
  last_active_role?: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  date_joined?: string;
  photo_url?: string;
  has_instructor_profile?: boolean;
  assigned_courses_count?: number;
  gender?: string | null;
  home_address?: string | null;
  groups?: { id: number; name: string }[];
  user_permissions?: string[];
  instructor_profile?: {
    id: number;
    name: string;
    students_count: string;
    rating: string;
  } | null;
}

export interface AdminUserKpis {
  total: number;
  superadmins: number;
  instructors: number;
  admins: number;
  students: number;
  suspended: number;
}

export interface RoleAssignmentPayload {
  primary_role: "STUDENT" | "INSTRUCTOR" | "ADMIN" | "SUPERADMIN" | string;
  roles: string[];
}

export interface ProvisionUserPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone_number?: string;
  role: string;
  roles?: string[];
  password?: string;
}

export interface PermissionItem {
  code: string;
  name: string;
  description: string;
}

export interface PermissionCategory {
  category: string;
  description: string;
  permissions: PermissionItem[];
}

export interface RoleSummary {
  id: string;
  role: string;
  name: string;
  description: string;
  usersCount: number;
  permissions: string[];
}

export interface RoleGroupData {
  id: number;
  name: string;
  users_count: number;
  permissions_list: string[];
}

export interface RolePermissionMatrixResponse {
  roles: RoleSummary[];
  catalog: PermissionCategory[];
  groups: RoleGroupData[];
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not yet initialized
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

export const userService = {
  // Fetch users directory with KPIs
  async getUsers(params?: {
    search?: string;
    role?: string;
    status?: string;
    signal?: AbortSignal;
  }): Promise<{ users: AdminUserData[]; kpis: AdminUserKpis }> {
    const client = getClient();
    const queryParams: Record<string, string> = {};
    if (params?.search) queryParams.search = params.search;
    if (params?.role && params.role !== "All Roles" && params.role !== "All") queryParams.role = params.role;
    if (params?.status && params.status !== "all") queryParams.status = params.status;

    const res = await client.get("/course/admin-users/", {
      params: queryParams,
      signal: params?.signal,
    });
    return res.data;
  },

  // Fetch individual user detail
  async getUserDetail(id: number | string, signal?: AbortSignal): Promise<AdminUserData> {
    const client = getClient();
    const res = await client.get(`/course/admin-users/${id}/`, { signal });
    return res.data;
  },

  // Provision new user
  async provisionUser(data: ProvisionUserPayload): Promise<{ message: string; user: AdminUserData; temporary_password?: string }> {
    const client = getClient();
    const res = await client.post("/course/admin-users/", data);
    return res.data;
  },

  // Update personal/profile fields
  async updateUser(id: number | string, data: Partial<AdminUserData>): Promise<{ message: string; user: AdminUserData }> {
    const client = getClient();
    const res = await client.patch(`/course/admin-users/${id}/`, data);
    return res.data;
  },

  // Assign primary role and secondary roles
  async assignRoles(id: number | string, data: RoleAssignmentPayload): Promise<{ message: string; user: AdminUserData }> {
    const client = getClient();
    const res = await client.post(`/course/admin-users/${id}/assign-role/`, data);
    return res.data;
  },

  // Assign granular permissions & groups
  async assignPermissions(
    id: number | string, 
    data: { permissions?: string[]; groups?: string[] }
  ): Promise<{ message: string; user: AdminUserData }> {
    const client = getClient();
    const res = await client.post(`/course/admin-users/${id}/assign-permissions/`, data);
    return res.data;
  },

  // Toggle active/suspended status
  async toggleStatus(id: number | string, isActive: boolean): Promise<{ message: string; user: AdminUserData }> {
    const client = getClient();
    const res = await client.patch(`/course/admin-users/${id}/`, { is_active: isActive });
    return res.data;
  },

  // Delete user account
  async deleteUser(id: number | string): Promise<{ message: string }> {
    const client = getClient();
    const res = await client.delete(`/course/admin-users/${id}/`);
    return res.data;
  },

  // Fetch roles and permissions matrix
  async getRolePermissionMatrix(signal?: AbortSignal): Promise<RolePermissionMatrixResponse> {
    const client = getClient();
    const res = await client.get("/course/roles-permissions/", { signal });
    return res.data;
  },

  // Create or update custom role group
  async createCustomRole(data: { name: string; permissions: string[] }): Promise<{ message: string; group: RoleGroupData }> {
    const client = getClient();
    const res = await client.post("/course/roles-permissions/", data);
    return res.data;
  },
};
